import { cache } from '../../cache-impl/cache-registry.js';
import { containsFlag } from '../../../../webmodule-common/other/ui/string-helper-functions.js';
import { customElement } from 'lit/decorators.js';
import { DataTableWrapper } from '../../../../webmodule-common/other/ui/datatable-view.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid.js';
import { buildQuoteRowActions, QuoteActionCard, QuoteRowAction } from '../../quotes/capabilities/quote-row-actions.js';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user.js';
import { getUserInformativeDisplayName } from '../../common/cache-tools.js';
import { QuoteActionDialog } from '../../quotes/views/quote-action-dialog.js';
import { QuoteContainer } from '../../../quotes/data/quote-container.js';
import { ProjectResourceView, ProjectResourceViewOptions } from '../../../projects/views/project-resource-view.js';
import { EventTemplate, Snippet } from '../../../../webmodule-common/other/ui/events.js';
import { type FabricationBucket, tallyFabricationBuckets } from '../../quotes/data/quote-fabrication-buckets.js';
import {
  bucketsByQuote,
  fetchQuoteFabricationCounts,
  quoteFabricationIconsStyles,
  renderQuoteFabricationIcons
} from '../../quotes/data/quote-fabrication-icons.js';
import { FabricationJobSummaryView } from '../../../api/fabrication-job-api.js';
import { fireQuickErrorToast } from '../../../../webmodule-common/other/toast-away.js';
import { getApiFactory } from '../../../api/api-injector.js';
import { getFabricationJobStatus } from '../../../fabrication-jobs/data/fabrication-job-helper-functions.js';
import {
  getPurchaseOrderLink as getFabricationPurchaseOrderLink,
  getPurchaseOrderNumberFormatted as getFabricationPurchaseOrderNumberFormatted,
  getPurchaseOrderStatusBadge as getFabricationPurchaseOrderStatusBadge
} from '../../fabrication-purchase-orders/data/fabrication-purchase-order-helper-functions.js';
import {
  getPurchaseOrderNumberFormatted,
  getPurchaseOrderStatus
} from '../../../purchase-orders/data/purchase-order-helper-functions.js';
import { getQuoteContainer, getQuoteContainerManager } from '../../quotes/quote-ui-adapter.js';
import { getQuoteLockBadge } from '../../quotes/views/quote-lock-badge.js';
import { getQuoteNumberFormatted, getQuoteStatus } from '../../../quotes/data/quote-helper-functions.js';
import { CSSResult, html } from 'lit';
import { ItemReference } from '../../../../webmodule-common/cache/definitions/cache-item-reference.js';
import { LangFranchiseeProjectResourceView } from '../../../../localization/scripts/dealer-franchisee/projects/views/franchisee-project-resource-view-lang';
import { launchQuote, resourceQuote } from '../../../quotes/ui/launcher.js';
import { moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter.js';
import { monitorQuoteCopyCompletion } from '../../quotes/data/quote-copy-completion-monitor.js';
import { ProjectContainerManager } from '../../../projects/data/project-container.js';
import { ProjectResourceLink } from '../../cache-impl/resource-link.js';
import { ProjectState, Resource, ResourceType } from '../../../api/dealer-api-interface-project.js';
import { PurchaseOrder } from '../../../api/dealer-api-interface-franchisee.js';
import { PurchaseOrderSummaryView } from '../../../api/dealer-api-interface-all.js';
import {
  QuotePriceCalculation,
  QuoteSetSibling,
  QuoteState,
  ViewQuoteSummary
} from '../../../api/dealer-api-interface-quote.js';
import {
  buildQuoteStateContext,
  computeQuoteRowCapabilities
} from '../../quotes/capabilities/quote-row-capabilities.js';
import { QuoteStateActions } from '../../quotes/helper/quote-state-change-helper.js';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang.js';
import { refreshButton } from '../../../../webmodule-common/other/ui/command-action.js';
import { RequestPage, ResultPaginated } from '../../../../webmodule-common/other/ui/RequestPage.js';
import { resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver.js';
import { resourceFabricationJob } from '../../../fabrication-jobs/ui/launcher.js';
import { resourcePurchaseOrder } from '../../../purchase-orders/ui/launcher.js';
import { userDataStore } from '../../common/current-user-data-store.js';
import {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components.js';
import { when } from 'lit/directives/when.js';
import { WMEventSource } from '../../../api/event-source.js';

interface FranchiseeProjectResourceTableOptions {
  projectManager: ProjectContainerManager;
}

/**
 * Copy now carries the mode the user picked in the row-action dialog, instead of the view guessing.
 * A local type rather than a change to the shared EventResource, which other resource tables use.
 *
 * The three modes are the same three the old `QuoteCopyChoiceDialog` offered (that dialog is gone — the
 * row-action card dialog now presents these as explicit cards, so the choice is made once):
 *  - `copyOutOfProject`     : a standalone copy not attached to this project. Always available.
 *  - `copyNewProjectQuote`  : an independent new quote in the same project. Needs an active project.
 *  - `copyAlternative`      : a new alternative in the same quote set. Needs an active project AND a set
 *                             that can still take one; both are gated in computeQuoteRowCapabilities.
 */
export type CopyResourceEvent = (id: string, mode: QuoteCopyMode) => Promise<boolean>;

export type QuoteCopyMode = 'copyAlternative' | 'copyNewProjectQuote' | 'copyOutOfProject';

interface FranchiseProjectResourceQuoteTableOptions extends FranchiseeProjectResourceTableOptions {
  copyResourceEvent: CopyResourceEvent;
}

@customElement('wm-franchiseeprojectresourcequotetable')
export class FranchiseeProjectResourceQuoteTable extends DataTableWrapper<Resource> {
  projectManager: ProjectContainerManager;
  quoteCache = cache().quote;
  userCache = cache().userProfile;
  copyResourceEvent: CopyResourceEvent;
  // Fabrication buckets per quote id, for the status-cell icons. Supplied by the view, which fetches
  // the summaries once for the whole project and feeds both these and its progress strip, so the
  // endpoint is not called twice for the same quotes.
  private fabricationBuckets = new Map<string, FabricationBucket[]>();

  constructor(options: FranchiseProjectResourceQuoteTableOptions) {
    super();
    this.projectManager = options.projectManager;
    this.copyResourceEvent = options.copyResourceEvent;
  }

  // The project's resources load as a single page, so there is nothing to page through.
  // Drives pageLength, showPaginationInfo and pageSizeOptions together.
  override disablePagination(): boolean {
    return true;
  }

  // Styles for the fabrication icon chips, injected into the table's shadow root - the same hook and
  // the same styles the quote list uses, so the chips read identically on both surfaces.
  override customStyles(): CSSResult {
    return quoteFabricationIconsStyles;
  }

  public setFabricationBuckets(buckets: Map<string, FabricationBucket[]>): void {
    this.fabricationBuckets = buckets;
    this.requestUpdate();
  }

  public async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<Resource>> {
    await this.projectManager.needsProject();
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.Quote);
    if (!items)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };

    const quoteKeys = items.map(x => x.resourceId ?? emptyGuid) ?? [];

    //if there are any other callbacks we need to do, add them to the
    //all array which will let them run at the same time
    await Promise.all([this.quoteCache.preFetch(quoteKeys)]);

    // Prefetch supplier configs for the quotes now in cache, so resolveAllowSupplierReview() is
    // populated before any ellipsis opens (fabricator/supplier-review greying with no per-row fetch).
    const supplierIds = new Set(
      quoteKeys.map(id => this.getQuoteSummary(id)?.supplierId).filter((s): s is string => !!s)
    );
    await Promise.all([...supplierIds].map(supplierId => QuoteStateActions.loadSupplierConfig(supplierId)));

    const authorKeys =
      items
        .map(x => {
          const summary = this.getQuoteSummary(x.resourceId);
          return summary ? summary.assignedToUserId : null;
        })
        .filter(key => key !== null) ?? [];

    await this.userCache.preFetch(authorKeys);

    items.sort((a1: Resource, a2: Resource) => {
      const s1 = this.getQuoteSummary(a1.resourceId);
      const s2 = this.getQuoteSummary(a2.resourceId);
      const t1 = getQuoteNumberFormatted(s1);
      const t2 = getQuoteNumberFormatted(s2);
      return t1.localeCompare(t2);
    });
    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  public getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangFranchiseeProjectResourceView.date(),
        fieldName: 'resourceId',
        width: '110px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary) return '';

          const dt = new Date(summary.dateCreated);
          return `${dt.toLocaleDateString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.quoteNo(),
        fieldName: 'resourceId',
        minWidth: '130px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);
          const copying = summary ? containsFlag(summary?.state, QuoteState.Copying) : false;
          return this.quoteLink(resource, getQuoteNumberFormatted(this.getQuoteSummary(resource.resourceId)), copying);
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.quoteTitle(),
        fieldName: 'resourceId',
        minWidth: '220px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);
          const copying = summary ? containsFlag(summary?.state, QuoteState.Copying) : false;
          return this.quoteLink(resource, this.getQuoteSummary(resource.resourceId)?.title ?? '', copying);
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.author(),
        fieldName: 'resourceId',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;

          return this.getUserDisplayValue(this.getQuoteSummary(resource.resourceId)?.assignedToUserId ?? '');
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.pricingMetric(),
        fieldName: 'resourceId',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;

          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary) return html``;

          const val = html`(${summary.percentMarginOrMarkup}%)`;

          return summary.calculation === QuotePriceCalculation.Margin
            ? html` <webmodule-tag size="small" pill variant="primary">Margin ${val}</webmodule-tag>`
            : html` <webmodule-tag size="small" pill variant="neutral">Markup ${val}</webmodule-tag>`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.lastModified(),
        fieldName: 'lastModifiedDate',
        width: '170px',
        sortable: false,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary?.lastModifiedDate) return '';

          const dt = new Date(summary.lastModifiedDate);
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.status(),
        fieldName: 'resourceId',
        minWidth: '150px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary) return '';

          // Same pairing as the quote list: the quote's own state badge (Copying on a copy's
          // DESTINATION) plus its lock badge (Copying on the SOURCE), and the same fabrication
          // status icons underneath. Shared helpers so the two surfaces cannot drift on what a
          // row shows.
          return html`${getQuoteStatus(summary.state, true)}${getQuoteLockBadge(summary.lockState)}
          ${renderQuoteFabricationIcons(this.fabricationBuckets.get(resource.resourceId) ?? [])}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.netAmount(),
        fieldName: 'resourceId',
        width: '130px',
        align: 'right',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          return this.getQuotePrice(resource);
        }
      },
      {
        title: html` <webmodule-icon library="fa" name="fas-bars"></webmodule-icon>`,
        fieldName: 'resourceId',
        sortable: false,
        width: '52px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          return this.ellipsisMenu(resource);
        }
      }
    ];
  }

  ellipsisMenu(row: Resource) {
    const summary = this.getQuoteSummary(row.resourceId);

    // Guard: the quote may not be cached yet. Keep the (empty) dropdown and render no actions rather
    // than dereferencing a null summary — removing this guard is what crashed the prior PR's project view.
    if (!summary) {
      return html` <webmodule-dropdown placement="bottom-end" hoist>
        <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
        <webmodule-menu></webmodule-menu>
      </webmodule-dropdown>`;
    }

    // Siblings are cached alongside the summary (threaded from the summary endpoint) so we grey the
    // actions with no per-row load, using the same capability model as the quote list.
    return html`<webmodule-icon-button
      library="fa"
      name="fas-bars"
      @click=${async (e: Event) => {
        e.stopPropagation();
        await this.openActionDialog(summary);
      }}
    ></webmodule-icon-button>`;
  }

  /**
   * The row's 3-bar control opens the rich action dialog DIRECTLY — no popup menu in between
   * (Task 229184). Unlike the quote list, this view CAN attach a copy to its project, so it offers
   * all three copy modes as explicit cards. That replaces the old behaviour where a single Copy item
   * silently chose alternative / new-project-quote / standalone on the user's behalf and only
   * interrupted when the set was full.
   */
  private async openActionDialog(summary: ViewQuoteSummary): Promise<void> {
    const initial = await this.buildActionCards(summary, this.quoteCache.getLocalData(summary.id)?.siblings ?? []);

    const chosen = await QuoteActionDialog.Execute(
      initial.cards,
      LangFranchiseeQuoteListView.actionDialogTitle({
        quoteNumber: getQuoteNumberFormatted(summary),
        quoteTitle: summary.title ?? ''
      }),
      initial.lockOwnerName,
      () => this.rereadActionCards(summary.id)
    );
    if (chosen) await this.runRowAction(chosen, summary);
  }

  /**
   * Re-read THIS quote's summary and rebuild its cards, in the background behind the open dialog. The
   * row's cached lock state can be stale — an edit lock is released with no event and expires on a TTL
   * with nothing running to announce it — and a stale `Locked` wrongly greys the copy cards.
   */
  private async rereadActionCards(
    quoteId: string
  ): Promise<{ cards: QuoteActionCard[]; lockOwnerName: string } | null> {
    this.quoteCache.flush([quoteId]);
    const fresh = await this.quoteCache.getData(quoteId);
    if (!fresh?.quoteSummary) return null;
    return await this.buildActionCards(fresh.quoteSummary, fresh.siblings ?? []);
  }

  /**
   * Build the cards from a summary + its siblings. Shared by the immediate (cached) open and the
   * background re-read so the two can never compute different menus from the same data.
   */
  private async buildActionCards(
    summary: ViewQuoteSummary,
    siblings: QuoteSetSibling[]
  ): Promise<{ cards: QuoteActionCard[]; lockOwnerName: string }> {
    const allowSupplierReview = QuoteStateActions.resolveAllowSupplierReview(summary.supplierId);
    const projectState = this.projectManager.project.state;
    const projectActive = projectState !== ProjectState.Cancelled && projectState !== ProjectState.Completed;

    const capabilities = computeQuoteRowCapabilities(buildQuoteStateContext(summary, siblings), {
      allowSupplierReview,
      projectActive,
      lockState: summary.lockState
    });

    const cards = buildQuoteRowActions(summary, {
      capabilities,
      currentUserId: getCurrentUser()?.id ?? emptyGuid,
      allowSupplierReview,
      projectActive,
      // This view's runRowAction dispatches copies and state changes only, and the ellipsis it replaced
      // never offered delete either. Saying so here is what keeps a dead Delete card off a Draft row.
      supportsDelete: false
    });

    const needsOwnerName = cards.some(c => c.reason === 'lockedByOther');
    const lockOwnerName = needsOwnerName ? await getUserInformativeDisplayName(summary.lockOwnerId) : '';
    return { cards, lockOwnerName };
  }

  /**
   * Dispatch a chosen row action. Values are unchanged from the old menu items, so the locked runner
   * and its gates are untouched; only the copy modes are newly explicit.
   */
  async runRowAction(value: QuoteRowAction, summary: ViewQuoteSummary) {
    if (value === 'copyAlternative' || value === 'copyNewProjectQuote' || value === 'copyOutOfProject') {
      await this.copyResourceEvent(summary.id, value);
      return;
    }

    if (!(await QuoteStateActions.checkBeforeRun())) return;

    // Cancel / Accept / Reject reuse the shared locked runner, then refresh the resource table.
    const targetState = QuoteStateActions.toTargetState(value);
    if (targetState !== null) {
      await QuoteStateActions.runLockedQuoteAction(summary.id, summary.supplierId, {
        requiresSupplierOnline: true,
        duringLock: qcm => QuoteStateActions.applyStateChange(qcm, targetState),
        afterLock: () => this.refreshData()
      });
    }
  }

  public getQuoteSummary(id: string) {
    const quoteSummary = this.quoteCache.getLocalData(id)?.quoteSummary;
    return quoteSummary ? quoteSummary : null;
  }

  /**
   * Clears the resources held by this table from the cache.
   */
  public async clearResources(): Promise<void> {
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.Quote);
    if (items) {
      const quoteKeys = items.map(x => x.resourceId ?? emptyGuid);
      await this.quoteCache.updateLocal(...quoteKeys);
    }
  }

  protected quoteLink(row: Resource, value: string, copying?: boolean) {
    return copying
      ? html`<span class="quote-link disabled-quote">${value}</span>`
      : html`<a class="quote-link" href="${resolveURL(resourceQuote, row.resourceId)}" data-quoteid="${row.resourceId}"
          >${value}</a
        >`;
  }

  protected getUserDisplayValue(value: string) {
    if (value === emptyGuid) return '';
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue}` ?? '';
  }

  private getQuotePrice(row: Resource) {
    const priceSummary = this.getQuoteSummary(row.resourceId);
    return priceSummary ? moneyToTemplateResult(priceSummary.calculatedNetTotal, priceSummary.quoteCurrency) : '';
  }
}

@customElement('wm-franchiseeprojectresourcepurchaseordertable')
export class FranchiseeProjectResourcePurchaseOrderTable extends DataTableWrapper<Resource> {
  projectManager: ProjectContainerManager;
  private quoteCache = cache().quote;
  private purchaseOrderCache = cache().purchaseOrder;
  private userCache = cache().userProfile;

  private linkCache = cache().projectResourceLink;

  constructor(options: FranchiseeProjectResourceTableOptions) {
    super();
    this.projectManager = options.projectManager;
  }

  // The project's resources load as a single page, so there is nothing to page through.
  // Drives pageLength, showPaginationInfo and pageSizeOptions together.
  override disablePagination(): boolean {
    return true;
  }

  public async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<Resource>> {
    await this.projectManager.needsProject();
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.PurchaseOrder);
    if (!items)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };

    const poKeys = items.map(x => x.resourceId ?? emptyGuid) ?? [];

    await this.purchaseOrderCache
      .preFetch(poKeys)
      .then(async () => {
        return await this.linkCache.getMany(poKeys);
      })
      .then(async (data: ItemReference<ProjectResourceLink>[] | null) => {
        const quoteIds = data?.map(x => x.data.quoteId) ?? [];
        return this.quoteCache.preFetch(quoteIds);
      });

    const authorKeys =
      items
        .map(x => {
          const summary = this.getPurchaseOrder(x.resourceId);
          return summary ? summary.assignedToUserId : null;
        })
        .filter(key => key !== null) ?? [];

    await this.userCache.preFetch(authorKeys);

    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  public getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangFranchiseeProjectResourceView.purchaseOrderNo(),
        fieldName: 'resourceId',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          return this.getPurchaseOrderLink(
            resource,
            getPurchaseOrderNumberFormatted(this.getPurchaseOrder(resource.resourceId))
          );
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.quoteNo(),
        fieldName: 'resourceId',
        minWidth: '130px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          return this.quoteLink(
            this.getQuote(resource.resourceId)?.id ?? '',
            getQuoteNumberFormatted(this.getQuote(resource.resourceId))
          );
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.author(),
        fieldName: 'resourceId',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;

          return this.getUserDisplayValue(this.getPurchaseOrder(resource.resourceId)?.assignedToUserId ?? '');
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.lastModified(),
        fieldName: 'lastModifiedDate',
        width: '170px',
        sortable: false,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          const po = this.getPurchaseOrder(resource.resourceId);

          if (!po) return '';

          const dt = new Date(po.lastModifiedDate ?? po.dateCreated);
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.status(),
        fieldName: 'resourceId',
        minWidth: '150px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getPurchaseOrder(resource.resourceId);

          if (!summary) return '';

          return getPurchaseOrderStatus(summary.state, true);
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.netCost(),
        fieldName: 'resourceId',
        width: '160px',
        align: 'right',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const resource = item as Resource;
          return this.getPrice(resource);
        }
      }
    ];
  }

  public getPurchaseOrder(id: string) {
    const po = this.purchaseOrderCache.getLocal(id)?.data.purchaseOrder;
    return po ? (po as any as PurchaseOrder) : null;
  }

  public getQuote(poId: string) {
    const qid = this.linkCache.getLocal(poId);
    const quoteSummary = this.quoteCache.getLocal(qid?.data.quoteId)?.data.quoteSummary;
    return quoteSummary ? quoteSummary : null;
  }

  /**
   * Clears the resources held by this table from the cache.
   */
  public async clearResources(): Promise<void> {
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.PurchaseOrder);
    if (items) {
      const purchaseOrderKeys = items.map(x => x.resourceId ?? emptyGuid);
      await this.purchaseOrderCache.updateLocal(...purchaseOrderKeys);
    }
  }

  protected quoteLink(id: string, value: string) {
    return html`<a class="quote-link" href="${resolveURL(resourceQuote, id)}" data-quoteid="${id}">${value}</a>`;
  }

  private getPrice(row: Resource) {
    const po = this.getPurchaseOrder(row.resourceId);

    return po
      ? moneyToTemplateResult(po.calculatedNetTotal + po.calculatedAdjustmentTotal, po.purchaseOrderCurrency)
      : '0';
  }

  private getPurchaseOrderLink(row: Resource, value: string) {
    return html`<a
      class="purchase-order-link"
      href="${resolveURL(resourcePurchaseOrder, row.resourceId)}"
      data-purchase-order-id="${row.resourceId}"
      >${value}</a
    >`;
  }

  protected getUserDisplayValue(value: string) {
    if (value === emptyGuid) return '';
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue}` ?? '';
  }
}

@customElement('wm-franchiseeprojectresourcefabricationjobtable')
export class FranchiseeProjectResourceFabricationJobTable extends DataTableWrapper<Resource> {
  projectManager: ProjectContainerManager;
  private fabricationJobApi = getApiFactory().fabricationJob();
  private jobs = new Map<string, FabricationJobSummaryView>();

  constructor(options: FranchiseeProjectResourceTableOptions) {
    super();
    this.projectManager = options.projectManager;
  }

  // The project's resources load as a single page, so there is nothing to page through.
  // Drives pageLength, showPaginationInfo and pageSizeOptions together.
  override disablePagination(): boolean {
    return true;
  }

  public async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<Resource>> {
    await this.projectManager.needsProject();
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.FabricationJob) ?? [];
    this.jobs.clear();
    if (items.length === 0) {
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };
    }

    const jobIds = items.map(x => x.resourceId).filter(id => id && id !== emptyGuid);
    const summaries = await this.fabricationJobApi.getFabricationJobsByIds({ fabricationJobIds: jobIds });
    for (const summary of summaries ?? []) {
      this.jobs.set(summary.id, summary);
    }

    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  public getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangFranchiseeProjectResourceView.jobNo(),
        fieldName: 'resourceId',
        minWidth: '140px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          const job = this.jobs.get(r.resourceId);
          if (!job) return '';
          return html`<a
            class="fabrication-job-link"
            href="${resolveURL(resourceFabricationJob, r.resourceId)}"
            data-fab-job-id="${r.resourceId}"
            >${job.jobNumber}</a
          >`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.jobTitle(),
        fieldName: 'resourceId',
        minWidth: '220px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          return this.jobs.get(r.resourceId)?.title ?? '';
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.lastModified(),
        fieldName: 'resourceId',
        width: '170px',
        sortable: false,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          const job = this.jobs.get(r.resourceId);
          if (!job) return '';
          const dt = new Date(job.lastModifiedDate ?? job.dateCreated);
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.status(),
        fieldName: 'resourceId',
        minWidth: '150px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          const job = this.jobs.get(r.resourceId);
          if (!job) return '';
          return getFabricationJobStatus(job.state, true);
        }
      }
    ];
  }

  public async clearResources(): Promise<void> {
    this.jobs.clear();
  }
}

@customElement('wm-franchiseeprojectresourcefabricationpurchaseordertable')
export class FranchiseeProjectResourceFabricationPurchaseOrderTable extends DataTableWrapper<Resource> {
  projectManager: ProjectContainerManager;
  private fabricationPurchaseOrderApi = getApiFactory().fabricationPurchaseOrder();
  private purchaseOrders = new Map<string, PurchaseOrderSummaryView>();

  constructor(options: FranchiseeProjectResourceTableOptions) {
    super();
    this.projectManager = options.projectManager;
  }

  // The project's resources load as a single page, so there is nothing to page through.
  // Drives pageLength, showPaginationInfo and pageSizeOptions together.
  override disablePagination(): boolean {
    return true;
  }

  public async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<Resource>> {
    await this.projectManager.needsProject();
    const items =
      this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.FabricationPurchaseOrder) ?? [];
    this.purchaseOrders.clear();
    if (items.length === 0) {
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };
    }

    const poIds = items.map(x => x.resourceId).filter(id => id && id !== emptyGuid);
    const summaries = await this.fabricationPurchaseOrderApi.getPurchaseOrdersByIds({ purchaseOrderIds: poIds });
    for (const summary of summaries ?? []) {
      this.purchaseOrders.set(summary.id, summary);
    }

    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  public getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangFranchiseeProjectResourceView.fabricationPurchaseOrderNo(),
        fieldName: 'resourceId',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          const po = this.purchaseOrders.get(r.resourceId);
          if (!po) return '';
          return getFabricationPurchaseOrderLink(po, getFabricationPurchaseOrderNumberFormatted(po));
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.fabricationPurchaseOrderTitle(),
        fieldName: 'resourceId',
        minWidth: '220px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          return this.purchaseOrders.get(r.resourceId)?.title ?? '';
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.lastModified(),
        fieldName: 'resourceId',
        width: '170px',
        sortable: false,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          const po = this.purchaseOrders.get(r.resourceId);
          if (!po) return '';
          const dt = new Date(po.lastModifiedDate ?? po.dateCreated);
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.status(),
        fieldName: 'resourceId',
        minWidth: '150px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const r = item as Resource;
          const po = this.purchaseOrders.get(r.resourceId);
          if (!po) return '';
          return getFabricationPurchaseOrderStatusBadge(po.state);
        }
      }
    ];
  }

  public async clearResources(): Promise<void> {
    this.purchaseOrders.clear();
  }
}

@customElement('wm-franchiseeprojectresourceview')
export class FranchiseeProjectResourceView extends ProjectResourceView {
  private quoteTable: FranchiseeProjectResourceQuoteTable;
  private poTable: FranchiseeProjectResourcePurchaseOrderTable;
  private fabricationJobTable: FranchiseeProjectResourceFabricationJobTable;
  private fabricationPurchaseOrderTable: FranchiseeProjectResourceFabricationPurchaseOrderTable;
  // Project-wide fabrication progress: the item-status counts of every quote in the project, summed
  // into one set of buckets. Non-zero buckets only, so the strip stays a summary rather than a
  // column of zeroes. Loaded in refreshData from one aggregate call, alongside the tables.
  private fabricationProgress: FabricationBucket[] = [];
  private projectCache = cache().project;
  private projectApi = getApiFactory().project();

  constructor(options: ProjectResourceViewOptions) {
    super(options);

    this.quoteTable = this.getResourceQuoteTableFactory();
    this.poTable = this.getResourcePurchaseOrderTableFactory();
    this.fabricationJobTable = this.getResourceFabricationJobTableFactory();
    this.fabricationPurchaseOrderTable = this.getResourceFabricationPurchaseOrderTableFactory();
  }

  /**
   * Live refresh on quote events. The quote LIST has always subscribed to this; this view never did,
   * so its rows only changed when something happened to call refreshData() — the periodic poll, or an
   * action on this page.
   *
   * That produced two distinct complaints from the same cause. Copying INTO this project appeared to
   * update slowly (the badge waited for the poll rather than the event), and copying OUT of the
   * project never showed a `Copying` badge on the source AT ALL — that path attaches nothing to the
   * project, so nothing on this page ever called refreshData().
   *
   * Only the quote table is refreshed: quote events say nothing about purchase orders or fabrication,
   * and refreshing all four would make every quote event four times as expensive.
   */
  private _eventQuoteUpdated = async () => {
    await this.quoteTable.refreshData();
  };

  connectedCallback(): void {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  public override async invalidate(): Promise<void> {
    await super.invalidate();
    this.quoteTable.refreshData();
    this.poTable.refreshData();
    this.fabricationJobTable.refreshData();
    this.fabricationPurchaseOrderTable.refreshData();
    this.loadFabrication();
  }

  public async refreshData(): Promise<void> {
    await super.refreshData();

    await this.updateResources();
    await Promise.all([
      this.quoteTable.refreshData(),
      this.poTable.refreshData(),
      this.fabricationJobTable.refreshData(),
      this.fabricationPurchaseOrderTable.refreshData(),
      this.loadFabrication()
    ]);
  }

  /**
   * Loads the project's fabrication data once and feeds both surfaces from it: the aggregated
   * progress strip below the jobs, and the per-quote status icons in the Quotes table. The
   * summaries endpoint returns a flat list of counts across every quote asked for, so the strip is
   * that list tallied whole and the icons are the same list grouped per quote - one call, not two.
   * Fabricator mode only, matching the sections it feeds; a project with no fabrication yields no
   * non-zero buckets and nothing renders.
   */
  private async loadFabrication(): Promise<void> {
    this.fabricationProgress = [];
    this.quoteTable.setFabricationBuckets(new Map());
    if (!userDataStore.isFabricatorMode) {
      this.requestUpdate();
      return;
    }

    await this.projectManager.needsProject();
    const quoteIds = (this.projectManager.container.resources ?? [])
      .filter(resource => resource.typeOf === ResourceType.Quote)
      .map(resource => resource.resourceId)
      .filter(id => id && id !== emptyGuid);

    // One fetch, both surfaces: the strip tallies the whole list, the icons group it per quote.
    // An empty list (no quotes, or the call failed) tallies to nothing and renders neither.
    const counts = await fetchQuoteFabricationCounts(quoteIds);
    this.fabricationProgress = tallyFabricationBuckets(counts).filter(bucket => bucket.count > 0);
    this.quoteTable.setFabricationBuckets(bucketsByQuote(counts));
    this.requestUpdate();
  }

  public buttonMenu(): Snippet {
    return html`${refreshButton(async () => {
      await this.refreshData();
    })}`;
  }

  public override async updateResources(): Promise<void> {
    await this.projectManager.reloadResourcesAndDocuments();
    await this.quoteTable.clearResources();
    await this.poTable.clearResources();
    await this.fabricationJobTable.clearResources();
    await this.fabricationPurchaseOrderTable.clearResources();
  }

  protected getResourceQuoteTableFactory() {
    return new FranchiseeProjectResourceQuoteTable({
      projectManager: this.projectManager,
      copyResourceEvent: async (id: string, mode: QuoteCopyMode) => {
        return await this.copyQuote(id, mode);
      }
    });
  }

  protected getResourcePurchaseOrderTableFactory() {
    return new FranchiseeProjectResourcePurchaseOrderTable({
      projectManager: this.projectManager
    });
  }

  protected getResourceFabricationJobTableFactory() {
    return new FranchiseeProjectResourceFabricationJobTable({
      projectManager: this.projectManager
    });
  }

  protected getResourceFabricationPurchaseOrderTableFactory() {
    return new FranchiseeProjectResourceFabricationPurchaseOrderTable({
      projectManager: this.projectManager
    });
  }

  /**
   * The project's fabrication progress as a strip of chips, one per state its items are spread
   * across, each showing the item count. Reuses the quote list's bucket vocabulary (icon, badge
   * colour, label) so the two surfaces read the same. Renders nothing when the project has no
   * fabrication, so the strip and its heading only appear when there is something to show.
   */
  private fabricationProgressTemplate(): EventTemplate {
    if (this.fabricationProgress.length === 0) return html``;
    return html`
      <h2 class="section-header">${LangFranchiseeProjectResourceView.fabricationProgressSectionHeader()}</h2>
      <div class="d-flex flex-wrap gap-3 mb-3">
        ${this.fabricationProgress.map(
          bucket =>
            html`<div class="d-flex align-items-center gap-1">
              <span class="badge ${bucket.badgeClass}"
                ><webmodule-icon library="fa" name=${bucket.icon} aria-hidden="true"></webmodule-icon>
                ${bucket.count}</span
              >
              <span>${bucket.label}</span>
            </div>`
        )}
      </div>
    `;
  }

  protected template(): EventTemplate {
    return html`
      <h2 class="section-header">${LangFranchiseeProjectResourceView.quotesSectionHeader()}</h2>
      <div class="row">${this.quoteTable}</div>

      ${when(
        userDataStore.isFabricatorMode,
        () => html`
          <h2 class="section-header">${LangFranchiseeProjectResourceView.fabricationJobsSectionHeader()}</h2>
          <div class="row">${this.fabricationJobTable}</div>

          ${this.fabricationProgressTemplate()}

          <h2 class="section-header">${LangFranchiseeProjectResourceView.fabricationPurchaseOrdersSectionHeader()}</h2>
          <div class="row">${this.fabricationPurchaseOrderTable}</div>
        `,
        () => html`
          <h2 class="section-header">${LangFranchiseeProjectResourceView.purchaseOrdersSectionHeader()}</h2>
          <div class="row">${this.poTable}</div>
        `
      )}
    `;
  }

  /**
   * Carry out the copy mode the user explicitly chose in the row-action dialog.
   *
   * Previously this GUESSED: it made an alternative when the set allowed one, silently fell back to a
   * new project quote when it did not (after a confirmation the user had no context for), and made a
   * standalone copy when the project was closed. The dialog now states the three options with their
   * consequences and disables the illegal ones with a reason, so the choice is the user's and the
   * fallback confirmation is gone.
   */
  private async copyQuote(id: string, mode: QuoteCopyMode): Promise<boolean> {
    //REFACTOR -> put this logic into the container manager
    const qcm = getQuoteContainerManager(getQuoteContainer(id));
    await qcm.needsQuote();

    // Only the two in-project modes attach the copy to this project; a standalone copy leaves it.
    const staysInProject = mode !== 'copyOutOfProject';

    let copy: QuoteContainer | null;
    switch (mode) {
      case 'copyAlternative':
        copy = await qcm.makeAlternative();
        break;
      case 'copyNewProjectQuote':
        // Stays in the project, so it keeps the source's (project) shipping address.
        qcm.generateQuoteNumberOnSave();
        copy = await qcm.makeCopy();
        break;
      case 'copyOutOfProject':
        // Leaves the project - start with a blank shipping address rather than a stale project one.
        copy = await qcm.makeCopy(true);
        break;
    }

    if (!copy) {
      fireQuickErrorToast(LangFranchiseeProjectResourceView.unableToCopyQuote());
      return false;
    }

    if (staysInProject) {
      //TODO# when  we implement supplier reviews we will need to be able to execute this quote as an
      //after save event of a quote.
      const resource = await this.projectApi.createProjectResourceReference({
        resourceId: copy.quoteId,
        projectId: this.projectManager.projectId,
        typeOf: ResourceType.Quote,
        parentResourceId: emptyGuid,
        parentTypeOf: ResourceType.None
      });
      if (resource) {
        this.projectCache.updateLocal(this.projectManager.projectId);
        await this.refreshData();
      } else {
        console.log(`quote copy failed to generate project resource. deleting quote`);
        // if we were unable to create the project link, remove the alternative quote
        return await getQuoteContainerManager(copy).deleteQuote();
      }
    }

    if (containsFlag(copy.quote!.state, QuoteState.Copying)) {
      await monitorQuoteCopyCompletion(copy.quoteId);
    } else {
      launchQuote(copy.quoteId);
    }
    return true;
  }
}
